import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {MatTable, MatTableDataSource, MatTableModule} from '@angular/material/table';
import {CommonModule, NgStyle} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject} from 'rxjs';

import {Role} from './../../core/model/role';
import {MessageService} from './../../services/message.service';
import {DeleteRoleAction, GetAllRolesAction, UpdateRoleAction} from './../../core/store/roles/roles.action';
import {RolePermissionCell, RolePermissionRow} from '../models/roles-table.model';
import {isPermissionCell, updateRolePermissions} from '../utils/roles-table.utils';
import {ListViewsModule} from './../../shared/list-views/list-views.module';
import {ROLE_DIALOG_SETTINGS} from '../utils/role.constants';
import {RoleDialogComponent, RoleDialogData} from '../role-dialog/role-dialog.component';
import {OphSlideToggleComponent} from './../../shared/design/oph-slide-toggle/oph-slide-toggle.component';
import {
  ListViewWarningDialogComponent,
  ListViewWarningDialogData,
} from './../../shared/list-views/shared/warning-dialog/list-view-warning-dialog.component';
import {GetRoleFromMapPipe} from '../pipes/get-role-from-map.pipe';
import {IsChildDisabledPipe} from '../pipes/is-child-disabled.pipe';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'roles-table',
  standalone: true,
  imports: [
    MatTableModule,
    CommonModule,
    ListViewsModule,
    OphSlideToggleComponent,
    NgStyle,
    GetRoleFromMapPipe,
    IsChildDisabledPipe,
  ],
  templateUrl: './roles-table.component.html',
  styleUrl: './roles-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolesTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly mobileService = inject(MobileService);
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private dialog = inject(MatDialog);

  displayedColumns: string[] = ['permission'];
  dataSource = new MatTableDataSource<RolePermissionRow>([]);
  dataSource$ = new BehaviorSubject<RolePermissionRow[]>([]);
  paginatedDataSource = signal<Role[]>([]);
  @ViewChild('rolesTableEl') rolesTableEl: MatTable<Role>;

  searchValue$ = input<BehaviorSubject<string>>();
  allRoles = input.required<Role[]>();
  colors = input<string[]>([]);
  allRolesMap = computed(
    () =>
      this.allRoles()?.reduce(
        (acc, role) => {
          acc[role.displayName] = role;
          return acc;
        },
        {} as Record<string, Role>
      )
  );

  menuOptions = ['edit', 'delete'];
  singleUserMenuOptions = ['edit'];
  isLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.subscribeToDataSource();
    this.subscribeToSearchValue();
    this.preparePermissionTable();
    this.setTableFilterPredicate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.allRoles) {
      this.preparePermissionTable();
    }
  }

  ngOnDestroy(): void {
    if (this.dataSource$) {
      this.dataSource$?.unsubscribe();
    }
  }

  setTableFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: RolePermissionRow, filter: string) => {
      const searchTerm = filter.toLowerCase();
      const matchesName = data.name.toLowerCase().includes(searchTerm);
      const matchesDescription = data.description.toLowerCase().includes(searchTerm);
      const matchesRole = Object.keys(data.roles).some(roleName => roleName.toLowerCase().includes(searchTerm));
      return matchesName || matchesDescription || matchesRole;
    };
  }

  subscribeToDataSource(): void {
    this.dataSource$.asObservable().subscribe(rolePermissionRows => {
      this.dataSource.data = rolePermissionRows;
    });
  }

  subscribeToSearchValue(): void {
    this.searchValue$().subscribe((searchValue: string) => {
      this.dataSource.filter = searchValue.trim().toLowerCase();
    });
  }

  preparePermissionTable(): void {
    const roles = [...this.allRoles()].sort((a, b) => {
      if (a.isSystemRole !== b.isSystemRole) {
        return a.isSystemRole ? -1 : 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });
    const permissionMap = new Map<string, RolePermissionRow>();

    for (const role of roles) {
      const rolePermissionsArr = Object.entries(role.permissions || {});
      rolePermissionsArr.forEach(([key, value]) => {
        // Handle simple permission
        if (isPermissionCell(value)) {
          const cell: RolePermissionCell = {
            name: value.name,
            description: value.description,
            active: value.active,
          };
          if (!permissionMap.has(key)) {
            permissionMap.set(key, {
              key,
              name: value.name,
              description: value.description,
              roles: {},
              isChild: false,
            });
          }
          const row = permissionMap.get(key);
          if (row) {
            row.roles[role.displayName ?? ''] = cell;
          }
        }
        // Handle nested permissions (e.g., projects)
        if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([subKey, subValue]) => {
            if (subKey === 'name' || subKey === 'description' || subKey === 'active') return;
            if (isPermissionCell(subValue)) {
              const permKey = `${key}.${subKey}`;
              const cell: RolePermissionCell = {
                name: subValue.name,
                description: subValue.description,
                active: subValue.active,
              };
              if (!permissionMap.has(permKey)) {
                permissionMap.set(permKey, {
                  key: permKey,
                  name: subValue.name,
                  description: subValue.description,
                  roles: {},
                  isChild: true,
                  parentKey: key,
                });
              }
              const row = permissionMap.get(permKey);
              if (row) {
                row.roles[role.displayName ?? ''] = cell;
              }
            }
          });
          // Also add the parent permission itself if it has name/active
          if (isPermissionCell(value)) {
            const cell: RolePermissionCell = {
              name: value.name,
              description: value.description,
              active: value.active,
            };
            if (!permissionMap.has(key)) {
              permissionMap.set(key, {
                key,
                name: value.name,
                description: value.description,
                roles: {},
                isChild: false,
              });
            }
            const row = permissionMap.get(key);
            if (row) {
              row.roles[role.displayName ?? ''] = cell;
            }
          }
        }
      });
    }

    this.dataSource$.next(Array.from(permissionMap.values()));
    this.setTableColumns();
  }

  setTableColumns(): void {
    const roles = this.allRoles();
    this.displayedColumns = ['permission', ...roles.map(r => r.displayName ?? '')];
  }

  getChildRows(parentKey: string): RolePermissionRow[] {
    return this.dataSource.data.filter(row => row.isChild && row.parentKey === parentKey);
  }

  togglePermission(roleName: string, row: RolePermissionRow, toggleEvent: boolean): void {
    if (roleName.toLowerCase() === 'admin') {
      this.messageService.add('Cannot edit permissions of the Admin role', 'error');
      return;
    }

    this.isLoading.set(true);

    const roles = this.allRoles();
    const {key: permKey} = row;
    const checked = toggleEvent;
    const role = roles.find(r => r.displayName.toLowerCase() === roleName.toLowerCase());
    if (!role) {
      this.isLoading.set(false);
      this.messageService.add(`Role ${roleName} not found`, 'error');
      return;
    }

    // If toggling a parent permission off, also deactivate all its children for that role
    if (!row.isChild && !checked) {
      // Update parent first
      let updatedRole = updateRolePermissions({permKey, role, checked});
      // Find all children and deactivate them
      const childRows = this.getChildRows(row.key);
      for (const childRow of childRows) {
        updatedRole = updateRolePermissions({permKey: childRow.key, role: updatedRole, checked: false});
      }
      this.store$.dispatch(
        new UpdateRoleAction({
          role: updatedRole,
          onSuccess: (updatedRole: Role) => {
            this.isLoading.set(false);
            this.store$.dispatch(new GetAllRolesAction({}));
            this.messageService.add('Role option updated successfully', 'taskCompleted');
          },
          onFailure: (error: Error) => {
            this.isLoading.set(false);
            this.messageService.add('Something went wrong while updating role option', 'error');
          },
        })
      );
      return;
    }

    // Default: just update the toggled permission
    const rolePayload = updateRolePermissions({
      permKey,
      role,
      checked,
    });

    this.store$.dispatch(
      new UpdateRoleAction({
        role: rolePayload,
        onSuccess: (updatedRole: Role) => {
          this.isLoading.set(false);
          this.store$.dispatch(new GetAllRolesAction({}));
          this.messageService.add('Role option updated successfully', 'taskCompleted');
        },
        onFailure: (error: Error) => {
          this.isLoading.set(false);
          this.messageService.add('Something went wrong while updating role option', 'error');
        },
      })
    );
  }

  onMenu(type: 'edit' | 'delete', roleName: string): void {
    const selectedRole = this.allRoles().find(r => r.displayName.toLowerCase() === roleName.toLowerCase());
    if (!selectedRole) {
      this.messageService.add(`Role ${roleName} not found`, 'error');
      return;
    }
    if (selectedRole.displayName.toLowerCase() === 'admin') {
      this.messageService.add('Cannot edit or delete the admin role', 'error');
      return;
    }
    if (type === 'edit') {
      this.editRole(selectedRole);
    }
    if (type === 'delete') {
      this.deleteRole(selectedRole);
    }
  }

  editRole(role: Role): void {
    this.dialog.open(RoleDialogComponent, {
      data: {role: role, roles: this.allRoles()} as RoleDialogData,
      ...ROLE_DIALOG_SETTINGS,
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  deleteRole(role: Role): void {
    const dialog = this.dialog.open(ListViewWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'list-views-warning-dialog',
      data: {
        type: 'custom',
        customData: {
          title: 'Are you sure you want to permanently delete the following role?',
          message: `Role: "${role.displayName}". This action cannot be undone.`,
          confirmText: 'Yes, Delete',
          cancelText: 'Cancel',
        },
      } as ListViewWarningDialogData,
    });

    dialog.afterClosed().subscribe(confirm => {
      if (confirm) {
        this.store$.dispatch(
          new DeleteRoleAction({
            roleId: role.id,
            onSuccess: () => {
              this.store$.dispatch(new GetAllRolesAction({}));
              this.messageService.add(`Role "${role.displayName}" deleted successfully`, 'taskCompleted');
            },
            onFailure: (error: Error) => {
              this.messageService.add(`Error deleting role "${role.displayName}": ${error.message}`, 'error');
            },
          })
        );
      }
    });
  }

  trackByFn(index: number, role: RolePermissionRow): string | number {
    if (!role?.key) return index;
    return role?.key;
  }
}
